import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Empty,
  Input,
  Popconfirm,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
  message,
  theme,
} from "antd";
import {
  BarChartOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
} from "@ant-design/icons";
import { ApiService, Report } from "../../api";
import { useRefreshOnTabFocus, useTabTitle } from "../../tabs/tabContext";
import { Divider, Page, PageHeader, Panel, space } from "./ui";

const { Text } = Typography;

/**
 * The saved questions people ask of this system.
 *
 * Presented as rows rather than a grid of cards: these are a list you scan by
 * name, and a card grid would spend three times the vertical space to hold the
 * same two lines of text. The row itself is the click target; the buttons on
 * the right appear on hover so a list of twenty is not a wall of buttons.
 */
const ReportsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = theme.useToken();

  const [reports, setReports] = useState<Report[]>([]);
  const [query, setQuery] = useState("");
  const [hovered, setHovered] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  useTabTitle(t("Reports"));

  const userInfo = localStorage.getItem("userInfo")
    ? JSON.parse(localStorage.getItem("userInfo") || "{}")
    : null;
  const isAdmin = Boolean(
    userInfo?.isAdmin ?? userInfo?.is_staff ?? userInfo?.is_superuser
  );

  const load = useCallback(async () => {
    try {
      setReports(await ApiService.apiMainReportsList());
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useRefreshOnTabFocus(load);

  const remove = async (report: Report) => {
    try {
      await ApiService.apiMainReportsDestroy(report.id);
      message.success(t("Removed"));
      await load();
    } catch (e: any) {
      message.error(e?.body?.message ?? t("Something went wrong"));
    }
  };

  const needle = query.trim().toLowerCase();
  const visible = needle
    ? reports.filter(
        (report) =>
          report.name.toLowerCase().includes(needle) ||
          report.template_name.toLowerCase().includes(needle)
      )
    : reports;

  const row = (report: Report) => {
    const grouped = Boolean((report.group_by as any)?.source);
    return (
      <div
        key={report.id}
        onMouseEnter={() => setHovered(report.id)}
        onMouseLeave={() => setHovered(null)}
        onClick={() => navigate(`/reports/${report.id}`)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: space.snug,
          padding: `${space.snug}px ${space.base}px`,
          cursor: "pointer",
          background:
            hovered === report.id ? token.colorFillQuaternary : "transparent",
          transition: "background .12s",
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: token.borderRadius,
            display: "grid",
            placeItems: "center",
            background: token.colorFillQuaternary,
            color: token.colorTextTertiary,
          }}
        >
          {grouped ? <BarChartOutlined /> : <TableOutlined />}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 500,
              color: token.colorText,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {report.name}
          </div>
          <div
            style={{
              display: "flex",
              gap: space.tight,
              alignItems: "center",
              fontSize: 12,
              color: token.colorTextSecondary,
              marginTop: 2,
            }}
          >
            <Tag
              style={{
                marginInlineEnd: 0,
                fontSize: 11,
                lineHeight: "16px",
                paddingInline: 6,
              }}
            >
              {report.template_name}
            </Tag>
            <span
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {report.description ||
                t("{{count}} columns", { count: report.columns.length })}
            </span>
          </div>
        </div>

        {/* Actions stay invisible until the row is under the cursor: twenty
            rows of buttons is a toolbar, not a list. Keyboard users get them
            via the report page itself, which carries the same actions. */}
        <div
          style={{
            display: "flex",
            gap: space.hair,
            flexShrink: 0,
            opacity: hovered === report.id ? 1 : 0,
            transition: "opacity .12s",
          }}
          onClick={(event) => event.stopPropagation()}
        >
          {isAdmin && (
            <>
              <Tooltip title={t("Edit")}>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`/reports/${report.id}/edit`)}
                />
              </Tooltip>
              <Popconfirm
                title={t("Remove this report?")}
                description={t(
                  "It stops being listed. The requests it reads are untouched."
                )}
                okText={t("Remove")}
                cancelText={t("Cancel")}
                onConfirm={() => remove(report)}
              >
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <Page>
      <PageHeader
        title={t("Reports")}
        meta={t("Saved questions you can run over any period and export to Excel.")}
        actions={
          isAdmin ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/reports/new")}
            >
              {t("New report")}
            </Button>
          ) : undefined
        }
      />

      <Panel padding={0}>
        {loading ? (
          <div style={{ padding: space.base }}>
            <Skeleton active paragraph={{ rows: 3 }} />
          </div>
        ) : reports.length === 0 ? (
          <div style={{ padding: space.band }}>
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <div style={{ maxWidth: 360, margin: "0 auto" }}>
                  <div>{t("No reports yet.")}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isAdmin
                      ? t(
                          "A report is a template, some of its fields as columns, and a period."
                        )
                      : t("No reports have been set up yet.")}
                  </Text>
                </div>
              }
            >
              {isAdmin && (
                <Button type="primary" onClick={() => navigate("/reports/new")}>
                  {t("New report")}
                </Button>
              )}
            </Empty>
          </div>
        ) : (
          <>
            {/* The search only earns its space once scanning gets hard. */}
            {reports.length > 7 && (
              <>
                <div style={{ padding: `${space.tight}px ${space.base}px` }}>
                  <Input
                    allowClear
                    bordered={false}
                    prefix={
                      <SearchOutlined style={{ color: token.colorTextQuaternary }} />
                    }
                    placeholder={t("Search reports")}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    style={{ paddingInline: 0 }}
                  />
                </div>
                <Divider />
              </>
            )}
            {visible.map((report, index) => (
              <div key={report.id}>
                {index > 0 && <Divider style={{ marginInlineStart: 60 }} />}
                {row(report)}
              </div>
            ))}
            {visible.length === 0 && (
              <div style={{ padding: space.wide, textAlign: "center" }}>
                <Text type="secondary">{t("No reports match that.")}</Text>
              </div>
            )}
          </>
        )}
      </Panel>
    </Page>
  );
};

export default ReportsListPage;
